import React, { useState } from 'react';
import {
    Card, Table, Tag, Typography, Button, Space, Drawer, Form, Input, InputNumber,
    Select, message, Popconfirm, Alert, Tooltip, Empty, Modal
} from 'antd';
import {
    PlusOutlined, EditOutlined, TeamOutlined, SearchOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// ══════════════════════════════════════════════
//  Mock 数据
// ══════════════════════════════════════════════
// TODO: 联调时替换为接口数据

// 国家字典（复用全球调度/设备区域国家）
const COUNTRY_OPTIONS = [
    { value: 'US', label: '美国' },
    { value: 'JP', label: '日本' },
    { value: 'DE', label: '德国' },
    { value: 'GB', label: '英国' },
    { value: 'SG', label: '新加坡' },
    { value: 'KR', label: '韩国' },
    { value: 'FR', label: '法国' },
    { value: 'AU', label: '澳大利亚' },
];

// 全量企业（平台客户）
const ALL_ENTERPRISES = [
    { id: 'ENT_SZ01', name: 'test-深圳芯视界科技有限公司' },
    { id: 'ENT_DG02', name: 'test-东莞康林物联网有限公司' },
    { id: 'ENT_BJ03', name: 'test-北京智联科技有限公司' },
    { id: 'ENT_HZ04', name: 'test-杭州云睿电子有限公司' },
    { id: 'ENT_SH05', name: 'test-上海智科信息有限公司' },
    { id: 'ENT_WH06', name: 'test-武汉光谷科技有限公司' },
];

const MOCK_GROUPS = [
    { key: 'g1', name: '标准价', cnyPrice: 0.80, usdPrice: 0.12, exchangeRate: 7.20,
        countryPrices: [], enterpriseIds: ['ENT_SZ01', 'ENT_DG02'], createdAt: '2026-09-01 10:00:00' },
    { key: 'g2', name: '大客户价', cnyPrice: 0.60, usdPrice: 0.09, exchangeRate: 7.20,
        countryPrices: [], enterpriseIds: ['ENT_BJ03'], createdAt: '2026-09-02 14:30:00' },
    { key: 'g3', name: '战略合作价', cnyPrice: 0.50, usdPrice: 0.08, exchangeRate: 7.15,
        countryPrices: [], enterpriseIds: [], createdAt: '2026-09-03 09:15:00' },
    { key: 'g4', name: '海外专项价', cnyPrice: 0.80, usdPrice: 0.15, exchangeRate: 7.30,
        countryPrices: [{ country: 'US', usdPrice: 0.10 }, { country: 'JP', usdPrice: 0.08 }],
        enterpriseIds: ['ENT_HZ04'], createdAt: '2026-09-04 16:45:00' },
    { key: 'g5', name: '测试组', cnyPrice: 1.00, usdPrice: 0.20, exchangeRate: 7.20,
        countryPrices: [], enterpriseIds: [], createdAt: '2026-09-05 11:20:00' },
];

const getCountryLabel = (v) => COUNTRY_OPTIONS.find(c => c.value === v)?.label || v;

// ══════════════════════════════════════════════
//  主页面
// ══════════════════════════════════════════════
const TrafficGroup = () => {
    const [groups, setGroups] = useState(MOCK_GROUPS);
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null); // null=新建
    const [editForm] = Form.useForm();
    const usdPrice = Form.useWatch('usdPrice', editForm);
    const exchangeRate = Form.useWatch('exchangeRate', editForm);

    const [entDrawerVisible, setEntDrawerVisible] = useState(false);
    const [entGroup, setEntGroup] = useState(null);
    const [entForm] = Form.useForm();
    const searchType = Form.useWatch('searchType', entForm) || 'name';
    const [appliedFilter, setAppliedFilter] = useState(null); // { type, value }

    const [addEntModalVisible, setAddEntModalVisible] = useState(false);
    const [addEntKeyword, setAddEntKeyword] = useState('');
    const [addEntSelected, setAddEntSelected] = useState([]);

    // 新建
    const openCreate = () => {
        setEditingGroup(null);
        editForm.resetFields();
        editForm.setFieldsValue({ cnyPrice: 0.80, usdPrice: 0.12, exchangeRate: 7.20, countryPrices: [] });
        setEditDrawerVisible(true);
    };

    // 编辑
    const openEdit = (record) => {
        setEditingGroup(record);
        editForm.setFieldsValue({
            name: record.name,
            cnyPrice: record.cnyPrice,
            usdPrice: record.usdPrice,
            exchangeRate: record.exchangeRate,
            countryPrices: record.countryPrices,
        });
        setEditDrawerVisible(true);
    };

    // 保存（新建/编辑）
    const handleSave = async () => {
        try {
            const v = await editForm.validateFields();
            const data = {
                name: v.name,
                cnyPrice: v.cnyPrice,
                usdPrice: v.usdPrice,
                exchangeRate: v.exchangeRate,
                countryPrices: v.countryPrices || [],
            };
            if (editingGroup) {
                setGroups(prev => prev.map(g => g.key === editingGroup.key ? { ...g, ...data } : g));
                message.success('收费规则已更新');
            } else {
                setGroups(prev => [...prev, {
                    key: `g${Date.now()}`, ...data, enterpriseIds: [],
                    createdAt: new Date().toLocaleString('zh-CN', { hour12: false }),
                }]);
                message.success('收费规则已创建');
            }
            setEditDrawerVisible(false);
        } catch (e) { /* 校验失败 */ }
    };

    // 删除
    const handleDelete = (record) => {
        if (record.enterpriseIds.length > 0) {
            message.warning('该收费规则下有关联企业，请先移除企业');
            return;
        }
        setGroups(prev => prev.filter(g => g.key !== record.key));
        message.success('收费规则已删除');
    };

    // 关联企业
    const openEnterprise = (record) => {
        setEntGroup(record);
        entForm.resetFields();
        setAppliedFilter(null);
        setEntDrawerVisible(true);
    };

    const currentEntGroup = groups.find(g => g.key === entGroup?.key) || entGroup;

    // 企业名称下拉选项（模糊查询）
    const enterpriseNameOptions = ALL_ENTERPRISES.map(e => ({ value: e.id, label: e.name }));

    // 查询 / 重置
    const handleEntSearch = (values) => {
        setAppliedFilter({ type: values.searchType, value: values.searchValue });
    };
    const handleEntReset = () => {
        entForm.resetFields();
        setAppliedFilter(null);
    };

    // 已关联企业列表（按查询条件过滤）
    const linkedEnterprises = (currentEntGroup?.enterpriseIds || []).map(id => {
        const e = ALL_ENTERPRISES.find(en => en.id === id);
        return e || { id, name: '-' };
    }).filter(e => {
        if (!appliedFilter?.value) return true;
        return e.id === appliedFilter.value;
    });

    // 可添加企业（排除已关联 + 按企业名称/ID 过滤）
    const linkedIds = new Set(currentEntGroup?.enterpriseIds || []);
    const availableEnterprises = ALL_ENTERPRISES.filter(e => {
        if (linkedIds.has(e.id)) return false;
        if (!addEntKeyword) return true;
        return e.name.includes(addEntKeyword) || e.id.includes(addEntKeyword);
    });

    // 移除企业
    const removeEnterprise = (entId) => {
        setGroups(prev => prev.map(g =>
            g.key === currentEntGroup.key ? { ...g, enterpriseIds: g.enterpriseIds.filter(id => id !== entId) } : g
        ));
    };

    // 打开添加企业弹窗
    const openAddEntModal = () => {
        setAddEntSelected([]);
        setAddEntKeyword('');
        setAddEntModalVisible(true);
    };

    // 确认添加企业（自动从其他规则移出，保证一企业仅属一个规则）
    const handleAddEnterprises = () => {
        if (addEntSelected.length === 0) {
            message.warning('请选择要添加的企业');
            return;
        }
        const ids = addEntSelected;
        setGroups(prev => prev.map(g => {
            if (g.key === currentEntGroup.key) {
                return { ...g, enterpriseIds: [...new Set([...g.enterpriseIds, ...ids])] };
            }
            return { ...g, enterpriseIds: g.enterpriseIds.filter(id => !ids.includes(id)) };
        }));
        message.success(`已添加 ${ids.length} 家企业`);
        setAddEntSelected([]);
        setAddEntKeyword('');
        setAddEntModalVisible(false);
    };

    const columns = [
        { title: '规则名称', dataIndex: 'name', key: 'name', render: (n) => <Text strong>{n}</Text> },
        { title: '国内单价', dataIndex: 'cnyPrice', key: 'cnyPrice', align: 'right',
            render: (v) => <Text>¥ {v?.toFixed(2)} <Text type="secondary" style={{ fontSize: 12 }}>/G</Text></Text> },
        { title: '海外默认单价', dataIndex: 'usdPrice', key: 'usdPrice', align: 'right',
            render: (v) => <Text>$ {v?.toFixed(2)} <Text type="secondary" style={{ fontSize: 12 }}>/G</Text></Text> },
        { title: '汇率(USD→CNY)', dataIndex: 'exchangeRate', key: 'exchangeRate', align: 'right',
            render: (v) => <Text>{v?.toFixed(2)}</Text> },
        { title: '国家精细价', key: 'countryPrices', render: (_, r) => (
            r.countryPrices?.length
                ? <Space size={4} wrap>{r.countryPrices.map(c => (
                    <Tag key={c.country} color="geekblue" style={{ border: 'none' }}>
                        {getCountryLabel(c.country)} $ {c.usdPrice.toFixed(2)}/G
                    </Tag>
                ))}</Space>
                : <Text type="secondary">—</Text>
        )},
        { title: '关联企业', dataIndex: 'enterpriseIds', key: 'enterpriseIds', align: 'center',
            render: (ids) => <Text>{ids?.length || 0}</Text> },
        { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', render: v => v || '-' },
        { title: '操作', key: 'action', width: 200, fixed: 'right', render: (_, r) => (
            <Space size={0}>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
                <Button type="link" size="small" icon={<TeamOutlined />} onClick={() => openEnterprise(r)}>关联企业</Button>
                {r.enterpriseIds.length > 0 ? (
                    <Tooltip title="有关联企业，需先移除企业"><Button type="link" size="small" danger disabled>删除</Button></Tooltip>
                ) : (
                    <Popconfirm title={`确定删除收费规则「${r.name}」？`} onConfirm={() => handleDelete(r)} okText="确定" cancelText="取消">
                        <Button type="link" size="small" danger>删除</Button>
                    </Popconfirm>
                )}
            </Space>
        )},
    ];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: 16 }}>
                <Title level={4}>设备流量商务</Title>
            </div>

            <Alert type="info" showIcon style={{ marginBottom: 16 }}
                message="收费规则为设备流量转发的价格模板：国内按 CNY/G、海外按 USD/G 计费，海外可对国家单独定价；企业关联收费规则后，其设备流量按该规则单价计费，未关联的企业不扣费。" />

            <Card bordered={false} style={{ borderRadius: 8 }}
                title={<Space><Text strong style={{ fontSize: 16 }}>收费规则</Text></Space>}
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建收费规则</Button>}
            >
                <Table
                    columns={columns}
                    dataSource={groups}
                    rowKey="key"
                    pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                />
            </Card>

            {/* 新建/编辑收费规则 Drawer */}
            <Drawer
                title={editingGroup ? '编辑收费规则' : '新建收费规则'}
                open={editDrawerVisible}
                onClose={() => setEditDrawerVisible(false)}
                width={640}
                destroyOnClose
                footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={() => setEditDrawerVisible(false)}>取消</Button>
                    <Button type="primary" onClick={handleSave}>保存</Button>
                </div>}
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item name="name" label="规则名称" rules={[{ required: true, message: '请输入规则名称' }]}>
                        <Input placeholder="如：标准价" maxLength={30} />
                    </Form.Item>
                    <Form.Item name="cnyPrice" label="国内单价（CNY/G）" rules={[{ required: true, message: '请输入国内单价' }]}
                        extra="国内设备流量按此单价计费（元/GB）">
                        <InputNumber min={0.0001} precision={2} style={{ width: '100%' }} addonBefore="¥" addonAfter="/G" />
                    </Form.Item>
                    <Form.Item name="usdPrice" label="海外默认单价（USD/G）" rules={[{ required: true, message: '请输入海外默认单价' }]}
                        extra={usdPrice && exchangeRate
                            ? `按汇率 ${exchangeRate.toFixed(2)} 折算 ≈ ¥ ${(usdPrice * exchangeRate).toFixed(2)}/G`
                            : '海外未单独定价的国家按此单价计费（美元/GB）'}>
                        <InputNumber min={0.0001} precision={2} style={{ width: '100%' }} addonBefore="$" addonAfter="/G" />
                    </Form.Item>
                    <Form.Item name="exchangeRate" label="汇率（USD→CNY）" rules={[{ required: true, message: '请输入汇率' }]}
                        extra="海外流量金额按此汇率折算为 CNY 出账">
                        <InputNumber min={0.0001} precision={2} style={{ width: '100%' }}
                            addonBefore={<span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>1 USD =</span>}
                            addonAfter="CNY" />
                    </Form.Item>

                    <DividerText>国家精细价（可选，覆盖海外默认价）</DividerText>
                    <Form.List name="countryPrices">
                        {(fields, { add, remove }) => (
                            <div>
                                {fields.length === 0 && (
                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未设置国家精细价，海外统一按默认价计费" />
                                )}
                                {fields.map(({ key, name, ...restField }) => (
                                    <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'baseline' }}>
                                        <Form.Item {...restField} name={[name, 'country']} style={{ flex: 1, marginBottom: 0 }}
                                            rules={[{ required: true, message: '请选择国家' }]}>
                                            <Select placeholder="选择国家" options={COUNTRY_OPTIONS} allowClear showSearch optionFilterProp="label" />
                                        </Form.Item>
                                        <Form.Item {...restField} name={[name, 'usdPrice']} style={{ flex: '0 0 200px', marginBottom: 0 }}
                                            rules={[{ required: true, message: '请输入单价' }]}>
                                            <InputNumber min={0.0001} precision={2} style={{ width: '100%' }} addonBefore="$" addonAfter="/G" placeholder="单价" />
                                        </Form.Item>
                                        <Button type="link" danger onClick={() => remove(name)}>删除</Button>
                                    </div>
                                ))}
                                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ country: undefined, usdPrice: undefined })}>
                                    添加国家精细价
                                </Button>
                            </div>
                        )}
                    </Form.List>
                </Form>
            </Drawer>

            {/* 关联企业 Drawer */}
            <Drawer
                title={`关联企业 - ${currentEntGroup?.name || ''}`}
                open={entDrawerVisible}
                onClose={() => setEntDrawerVisible(false)}
                width={720}
            >
                <Form form={entForm} layout="inline" onFinish={handleEntSearch} style={{ marginBottom: 16 }}>
                    <Form.Item style={{ marginRight: 8, marginBottom: 0 }}>
                        <Space.Compact style={{ width: 340 }}>
                            <Form.Item name="searchType" initialValue="name" noStyle>
                                <Select
                                    style={{ width: 110 }}
                                    options={[{ value: 'name', label: '企业名称' }, { value: 'id', label: '企业ID' }]}
                                    onChange={() => entForm.setFieldValue('searchValue', undefined)}
                                />
                            </Form.Item>
                            <Form.Item name="searchValue" noStyle>
                                {searchType === 'name' ? (
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="请选择企业名称"
                                        optionFilterProp="label"
                                        filterOption={(input, option) => (option?.label ?? '').includes(input)}
                                        options={enterpriseNameOptions}
                                        style={{ width: 230 }}
                                    />
                                ) : (
                                    <Input placeholder="请输入企业ID" allowClear style={{ width: 230 }} />
                                )}
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item style={{ marginRight: 8, marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
                            <Button onClick={handleEntReset}>重置</Button>
                        </Space>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={openAddEntModal}>添加企业</Button>
                    </Form.Item>
                </Form>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                    一个企业仅可关联一个收费规则；企业加入本规则后将自动从原规则移出。
                </Text>
                <Table
                    size="small"
                    rowKey="id"
                    dataSource={linkedEnterprises}
                    locale={{ emptyText: '暂无关联企业' }}
                    pagination={false}
                    columns={[
                        { title: '企业名称', key: 'name', render: (_, r) => (
                            <div><div style={{ fontWeight: 500 }}>{r.name}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text></div>
                        )},
                        { title: '操作', key: 'action', width: 80, render: (_, r) => (
                            <Popconfirm title="确定将该企业移出收费规则？" onConfirm={() => removeEnterprise(r.id)} okText="确定" cancelText="取消">
                                <Button type="link" size="small" danger>移除</Button>
                            </Popconfirm>
                        )},
                    ]}
                />
            </Drawer>

            {/* 添加企业 Modal */}
            <Modal
                title={`添加企业 - ${currentEntGroup?.name || ''}`}
                open={addEntModalVisible}
                onCancel={() => setAddEntModalVisible(false)}
                onOk={handleAddEnterprises}
                okText="确认添加"
                cancelText="取消"
                width={640}
                destroyOnClose
            >
                <Input
                    placeholder="搜索企业名称/企业ID"
                    allowClear
                    prefix={<SearchOutlined />}
                    style={{ marginBottom: 16 }}
                    value={addEntKeyword}
                    onChange={e => setAddEntKeyword(e.target.value)}
                />
                <Table
                    size="small"
                    rowKey="id"
                    dataSource={availableEnterprises}
                    rowSelection={{
                        selectedRowKeys: addEntSelected,
                        onChange: (keys) => setAddEntSelected(keys),
                    }}
                    locale={{ emptyText: '暂无可添加的企业' }}
                    pagination={false}
                    columns={[
                        { title: '企业名称', key: 'name', render: (_, r) => (
                            <div><div style={{ fontWeight: 500 }}>{r.name}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text></div>
                        )},
                    ]}
                />
            </Modal>
        </div>
    );
};

// 小型分隔标题
const DividerText = ({ children }) => (
    <div style={{ margin: '16px 0 12px', paddingBottom: 8, borderBottom: '1px solid #f0f0f0', fontSize: 14, fontWeight: 500, color: '#333' }}>
        {children}
    </div>
);

export default TrafficGroup;
